/**
 * Context-window budgeting helpers for Meridian.
 *
 * Message-count alone is not enough: a single turn with tool JSON + file
 * extracts can blow the input budget inside the message cap. We estimate size
 * from serialized content and trim oldest turns until under budget.
 *
 * The budget is TOKEN-denominated and env-tunable (see
 * {@link resolveContextWindowConfig}). Token counts are zero-dependency
 * ESTIMATES — no tokenizer (heavy, per-model). Prose is counted at ~4
 * chars/token; JSON/tool-result content at ~3.5 chars/token (structured
 * content tokenizes denser: braces, quotes, keys). Because these are
 * estimates, the default budget (100k) deliberately sits well under the 200k
 * model max — the gap is the safety margin.
 *
 * Future seam: per-message calibration against real `usage` events
 * (meridian_usage_events already records input_tokens per step) could replace
 * the flat ratios with an observed chars/token for this workload. Not built —
 * the flat ratios + safety margin are sufficient today.
 */

import type { ProviderKind } from '../providers';
import {
  DEFAULT_CONTEXT_WINDOW_MAX_TOKENS,
  DEFAULT_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK,
  DEFAULT_CONTEXT_WINDOW_MESSAGES,
} from './constants';
import { logger } from '../logger';

/** Rough chars→tokens ratio for prose text (Claude ~4 chars/token). */
export const CHARS_PER_TOKEN_PROSE = 4;

/**
 * Rough chars→tokens ratio for structured (JSON / tool-result) content —
 * denser tokenization than prose.
 */
export const CHARS_PER_TOKEN_STRUCTURED = 3.5;

/**
 * @deprecated Prose-only ratio kept for UI display call sites. Prefer
 * {@link estimateContentTokens} / {@link estimateMessagesTokens}, which are
 * content-aware.
 */
export const CHARS_PER_TOKEN_ESTIMATE = CHARS_PER_TOKEN_PROSE;

/** Resolved per-request context-window budget. */
export interface ContextWindowConfig {
  /** Max messages kept in the send window. */
  maxMessages: number;
  /** Max estimated input tokens of message history kept in the send window. */
  maxTokens: number;
}

/** Parse a positive-integer env var; warn + fall back on garbage. */
function readPositiveIntEnv(name: string, fallback: number): number {
  const raw = process.env[name]?.trim();
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    // Config mistake, not a runtime failure → warn (no #Hub Errors Slack noise).
    logger.warn('invalid context-window env value; using default', {
      op: 'resolveContextWindowConfig',
      envVar: name,
      rawValue: raw,
      fallback,
    });
    return fallback;
  }
  return parsed;
}

/**
 * Resolve the context-window budget for a request. Call ONCE per request and
 * pass the result down (prepareStep, pre-trim, summarization threshold) so a
 * mid-request env change can't produce a split-brain window.
 *
 * - `MERIDIAN_CONTEXT_WINDOW_MESSAGES` (default {@link DEFAULT_CONTEXT_WINDOW_MESSAGES})
 * - `MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS` (default {@link DEFAULT_CONTEXT_WINDOW_MAX_TOKENS})
 * - `MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK` (default
 *   {@link DEFAULT_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK}) — clamp applied when
 *   `providerKind === 'deepseek'` (smaller model contexts). Wire `providerKind`
 *   from `resolveProviderKind()` in `lib/providers`; do not re-read
 *   `MERIDIAN_LLM_PROVIDER` here.
 */
export function resolveContextWindowConfig(options?: {
  providerKind?: ProviderKind;
}): ContextWindowConfig {
  const maxMessages = readPositiveIntEnv(
    'MERIDIAN_CONTEXT_WINDOW_MESSAGES',
    DEFAULT_CONTEXT_WINDOW_MESSAGES
  );
  let maxTokens = readPositiveIntEnv(
    'MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS',
    DEFAULT_CONTEXT_WINDOW_MAX_TOKENS
  );

  if (options?.providerKind === 'deepseek') {
    const deepseekCap = readPositiveIntEnv(
      'MERIDIAN_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK',
      DEFAULT_CONTEXT_WINDOW_MAX_TOKENS_DEEPSEEK
    );
    maxTokens = Math.min(maxTokens, deepseekCap);
  }

  return { maxMessages, maxTokens };
}

export function estimateContentChars(content: unknown): number {
  if (content == null) return 0;
  if (typeof content === 'string') return content.length;
  try {
    return JSON.stringify(content).length;
  } catch {
    return String(content).length;
  }
}

export function estimateMessagesChars(
  messages: ReadonlyArray<{ content?: unknown; parts?: unknown }>
): number {
  let total = 0;
  for (const message of messages) {
    if ('content' in message && message.content !== undefined) {
      total += estimateContentChars(message.content);
    } else if ('parts' in message && message.parts !== undefined) {
      total += estimateContentChars(message.parts);
    }
  }
  return total;
}

/** Estimate tokens for structured (non-prose) content via JSON serialization. */
function estimateStructuredTokens(value: unknown): number {
  let serialized: string;
  try {
    serialized = JSON.stringify(value) ?? String(value);
  } catch {
    serialized = String(value);
  }
  return Math.ceil(serialized.length / CHARS_PER_TOKEN_STRUCTURED);
}

/** Estimate tokens for one content block: prose text at ~4 c/t, else ~3.5 c/t. */
function estimateBlockTokens(block: unknown): number {
  if (block == null) return 0;
  if (typeof block === 'string') {
    return Math.ceil(block.length / CHARS_PER_TOKEN_PROSE);
  }
  if (typeof block === 'object' && !Array.isArray(block)) {
    const record = block as Record<string, unknown>;
    if (record.type === 'text' && typeof record.text === 'string') {
      return Math.ceil(record.text.length / CHARS_PER_TOKEN_PROSE);
    }
  }
  // Tool calls, tool results, file parts, unknown shapes → structured density.
  return estimateStructuredTokens(block);
}

/**
 * Content-aware token estimate for a message body (`content` or `parts`).
 * Plain strings and `text` blocks count as prose; everything else (tool
 * JSON, file extracts) counts at the denser structured ratio.
 */
export function estimateContentTokens(content: unknown): number {
  if (content == null) return 0;
  if (typeof content === 'string') {
    return Math.ceil(content.length / CHARS_PER_TOKEN_PROSE);
  }
  if (Array.isArray(content)) {
    let total = 0;
    for (const block of content) {
      total += estimateBlockTokens(block);
    }
    return total;
  }
  return estimateStructuredTokens(content);
}

/** Sum of {@link estimateContentTokens} across a message array. */
export function estimateMessagesTokens(
  messages: ReadonlyArray<{ content?: unknown; parts?: unknown }>
): number {
  let total = 0;
  for (const message of messages) {
    if ('content' in message && message.content !== undefined) {
      total += estimateContentTokens(message.content);
    } else if ('parts' in message && message.parts !== undefined) {
      total += estimateContentTokens(message.parts);
    }
  }
  return total;
}

/**
 * @deprecated Flat prose-ratio conversion kept for UI display call sites.
 * Prefer {@link estimateContentTokens} / {@link estimateMessagesTokens}.
 */
export function estimateTokensFromChars(chars: number): number {
  return Math.ceil(chars / CHARS_PER_TOKEN_ESTIMATE);
}

/**
 * Keep the newest messages within both the message-count and token budgets.
 * Defaults come from {@link resolveContextWindowConfig} (no provider clamp —
 * pass an explicit budget when provider-aware).
 * Caller still runs ensureConversationStartsWithUser + stripOrphanToolUse.
 */
export function trimMessagesToContextBudget<
  T extends { content?: unknown; parts?: unknown },
>(
  messages: readonly T[],
  options?: {
    maxMessages?: number;
    maxTokens?: number;
  }
): T[] {
  const defaults = resolveContextWindowConfig();
  const maxMessages = options?.maxMessages ?? defaults.maxMessages;
  const maxTokens = options?.maxTokens ?? defaults.maxTokens;

  let trimmed =
    messages.length > maxMessages ? messages.slice(-maxMessages) : [...messages];

  while (trimmed.length > 2 && estimateMessagesTokens(trimmed) > maxTokens) {
    trimmed = trimmed.slice(1);
  }

  return trimmed;
}

export function formatConversationSummaryForSystem(summary: string): string {
  const trimmed = summary.trim();
  if (!trimmed) return '';
  return `\n\n═══ CONVERSATION CONTEXT ═══\nEarlier turns (compressed summary — full recent messages follow):\n${trimmed}\n═══════════════════════════`;
}
